import { useMemo } from 'react'
import {
  Sparkles,
  FileText,
  MessageSquare,
  CheckCircle2,
  Circle,
  AlertCircle,
  ArrowUpRight,
  Loader2,
} from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { cn } from '@/lib/utils'
import type { Document } from '@/types/api'
import type { ExplorerDoc } from './types'

interface ContextDashboardProps {
  onNavigateToChat: () => void
  documents: Document[]
  documentsLoading: boolean
  documentsError: string | null
  onSelectUpload: (doc: ExplorerDoc) => void
  onSeeAllUploads: () => void
}

const domains = [
  {
    name: 'Strategy & Vision',
    status: 'complete',
    count: 3,
    lastUpdated: '2h ago',
    insights: 'Mission aligned with Series A goals.',
  },
  {
    name: 'Market Research',
    status: 'complete',
    count: 5,
    lastUpdated: '1d ago',
    insights: '$50B TAM identified.',
  },
  {
    name: 'Product',
    status: 'partial',
    count: 2,
    lastUpdated: '3d ago',
    insights: 'Roadmap lacks Q4 specifics.',
  },
  {
    name: 'Customers',
    status: 'partial',
    count: 4,
    lastUpdated: '1w ago',
    insights: 'ICP defined as Enterprise B2B.',
  },
  {
    name: 'Go-to-Market',
    status: 'missing',
    count: 0,
    lastUpdated: '-',
    insights: 'No strategy documents found.',
  },
  {
    name: 'Team & Ops',
    status: 'missing',
    count: 0,
    lastUpdated: '-',
    insights: 'Team structure undefined.',
  },
  {
    name: 'Financials',
    status: 'partial',
    count: 1,
    lastUpdated: '2w ago',
    insights: 'Revenue projections outdated.',
  },
]

function getDocumentTimestamp(document: Document): number {
  const rawDate =
    document.uploadedAt ??
    document.createdAt ??
    document.updatedAt ??
    document.processedAt
  if (!rawDate) return 0
  const parsed = Date.parse(rawDate)
  return Number.isNaN(parsed) ? 0 : parsed
}

function formatRelativeDate(dateStr?: string): string {
  if (!dateStr) return 'Unknown time'
  const timestamp = Date.parse(dateStr)
  if (Number.isNaN(timestamp)) return 'Unknown time'

  const diffMs = Date.now() - timestamp
  if (diffMs < 60_000) return 'Just now'
  if (diffMs < 3_600_000) return `${Math.max(1, Math.floor(diffMs / 60_000))} min ago`
  if (diffMs < 86_400_000) return `${Math.max(1, Math.floor(diffMs / 3_600_000))} hours ago`

  const diffDays = Math.floor(diffMs / 86_400_000)
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  return new Date(timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

function toExplorerDoc(document: Document): ExplorerDoc {
  return {
    id: document.id,
    title: document.name?.trim() || `${document.type.toUpperCase()} document`,
    type: document.type === 'pdf' ? 'pdf' : document.type === 'audio' ? 'audio' : 'text',
    confidence: document.extractedData?.categoryConfidence,
  }
}

export function ContextDashboard({
  onNavigateToChat,
  documents,
  documentsLoading,
  documentsError,
  onSelectUpload,
  onSeeAllUploads,
}: ContextDashboardProps) {
  return (
    <div className="flex-1 p-4 md:p-10 overflow-auto bg-background">
      <div className="max-w-6xl mx-auto space-y-10">
        {/* Header */}
        <DashboardHeader />

        {/* Suggestions + Recent */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <RecommendedActions onNavigateToChat={onNavigateToChat} />
          <RecentUploads
            documents={documents}
            loading={documentsLoading}
            error={documentsError}
            onSelectUpload={onSelectUpload}
            onSeeAllUploads={onSeeAllUploads}
          />
        </div>

        {/* Domain Grid */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {domains.map((domain, i) => (
            <DomainCard key={i} domain={domain} onNavigateToChat={onNavigateToChat} />
          ))}
        </div>
      </div>
    </div>
  )
}

/* ── private sub-components ─────────────────────────────────── */

function DashboardHeader() {
  return (
    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight mb-2">Context Overview</h2>
        <p className="text-xs md:text-sm text-muted-foreground">
          High-level summary of your startup&apos;s knowledge graph.
        </p>
      </div>
      <div className="flex items-center gap-4 bg-white dark:bg-secondary p-4 rounded-xl border border-slate-200 dark:border-white/15">
        <div className="text-right">
          <div className="text-xs text-muted-foreground uppercase tracking-wider font-semibold">
            Overall Health
          </div>
          <div className="text-2xl font-bold text-primary">73%</div>
        </div>
        <div className="h-12 w-12 rounded-full border-4 border-secondary flex items-center justify-center relative">
          <svg className="absolute inset-0 -rotate-90" viewBox="0 0 32 32">
            <circle
              cx="16"
              cy="16"
              r="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="4"
              className="text-secondary"
            />
            <circle
              cx="16"
              cy="16"
              r="14"
              fill="none"
              stroke="currentColor"
              strokeWidth="4"
              className="text-primary"
              strokeDasharray="88 100"
            />
          </svg>
          <Sparkles className="h-4 w-4 text-primary" />
        </div>
      </div>
    </div>
  )
}

function RecommendedActions({ onNavigateToChat }: { onNavigateToChat: () => void }) {
  return (
    <div className="lg:col-span-2 space-y-4">
      <h3 className="font-semibold text-lg flex items-center gap-2">
        <Sparkles className="h-4 w-4 text-primary" />
        Recommended Actions
      </h3>
      <div className="space-y-3">
        <div
          className="flex items-center gap-4 p-4 rounded-xl bg-white dark:bg-secondary border border-slate-200 dark:border-white/15 hover:border-slate-300 dark:hover:border-primary/35 transition-colors cursor-pointer"
          onClick={onNavigateToChat}
        >
          <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
            <MessageSquare className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-sm">Fill GTM Strategy Gaps</h4>
            <p className="text-xs text-muted-foreground">
              Your Go-to-Market section is empty. Let&apos;s discuss your sales channels.
            </p>
          </div>
          <Button size="sm" variant="ghost">
            Start
          </Button>
        </div>

        <div
          className="flex items-center gap-4 p-4 rounded-xl bg-white dark:bg-secondary border border-slate-200 dark:border-white/15 hover:border-slate-300 dark:hover:border-primary/35 transition-colors cursor-pointer"
          onClick={onNavigateToChat}
        >
          <div className="h-10 w-10 rounded-full bg-accent-amber/10 flex items-center justify-center text-accent-amber shrink-0">
            <FileText className="h-5 w-5" />
          </div>
          <div className="flex-1">
            <h4 className="font-medium text-sm">Review Financial Projections</h4>
            <p className="text-xs text-muted-foreground">
              New data available from market sizing analysis.
            </p>
          </div>
          <Button size="sm" variant="ghost">
            Review
          </Button>
        </div>
      </div>
    </div>
  )
}

function RecentUploads({
  documents,
  loading,
  error,
  onSelectUpload,
  onSeeAllUploads,
}: {
  documents: Document[]
  loading: boolean
  error: string | null
  onSelectUpload: (doc: ExplorerDoc) => void
  onSeeAllUploads: () => void
}) {
  const uploads = useMemo(
    () =>
      [...documents]
        .sort((a, b) => getDocumentTimestamp(b) - getDocumentTimestamp(a))
        .slice(0, 3),
    [documents]
  )

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-lg">Recent Uploads</h3>
        <button
          type="button"
          className="text-xs font-medium text-primary hover:underline"
          onClick={onSeeAllUploads}
        >
          See all uploads
        </button>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed">
        Uploads are automatically sorted into Explorer categories and appear in the side panel
        under the category each document was assigned to.
      </p>
      <div className="bg-white dark:bg-secondary rounded-xl border border-slate-200 dark:border-white/15 p-1">
        {loading && (
          <div className="flex items-center justify-center gap-2 p-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading uploads...
          </div>
        )}
        {!loading && error && (
          <div className="p-4 text-sm text-destructive">{error}</div>
        )}
        {!loading && !error && uploads.length === 0 && (
          <div className="p-4 text-sm text-muted-foreground">No uploads yet.</div>
        )}
        {!loading &&
          !error &&
          uploads.map((upload) => (
            <button
              key={upload.id}
              type="button"
              className="w-full text-left flex items-center gap-3 p-3 hover:bg-slate-100 dark:hover:bg-tertiary/60 rounded-lg transition-colors"
              onClick={() => onSelectUpload(toExplorerDoc(upload))}
            >
              <FileText className="h-8 w-8 text-muted-foreground/50 shrink-0" />
              <div className="overflow-hidden">
                <div className="font-medium text-sm truncate">
                  {upload.name?.trim() || `${upload.type.toUpperCase()} document`}
                </div>
                <div className="text-xs text-muted-foreground">
                  {formatRelativeDate(
                    upload.uploadedAt ??
                      upload.createdAt ??
                      upload.updatedAt ??
                      upload.processedAt
                  )}
                </div>
              </div>
            </button>
          ))}
      </div>
    </div>
  )
}

function DomainCard({
  domain,
  onNavigateToChat,
}: {
  domain: (typeof domains)[number]
  onNavigateToChat: () => void
}) {
  return (
    <Card className="p-4 border-slate-200 dark:border-white/15 bg-white dark:bg-secondary hover:bg-slate-50 dark:hover:bg-tertiary/60 transition-colors flex flex-col h-40 md:h-48">
      <div className="flex items-start justify-between mb-4">
        <div
          className={cn(
            'h-8 w-8 rounded-lg flex items-center justify-center',
            domain.status === 'complete'
              ? 'bg-accent-success/10 text-accent-success'
              : domain.status === 'partial'
                ? 'bg-accent-warning/10 text-accent-warning'
              : 'bg-slate-100 dark:bg-secondary text-muted-foreground'
          )}
        >
          {domain.status === 'complete' ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : domain.status === 'partial' ? (
            <Circle className="h-4 w-4" />
          ) : (
            <AlertCircle className="h-4 w-4" />
          )}
        </div>
        <div className="px-2 py-0.5 rounded-full bg-background border border-border/50 text-[10px] font-mono text-muted-foreground">
          {domain.count} docs
        </div>
      </div>

      <h3 className="font-semibold text-base mb-1">{domain.name}</h3>
      <p className="text-xs text-muted-foreground mb-auto">{domain.insights}</p>

      <div className="mt-4 pt-4 border-t border-border/20 flex items-center justify-between text-xs text-muted-foreground">
        <span>
          {domain.lastUpdated === '-' ? 'Needs input' : `Updated ${domain.lastUpdated}`}
        </span>
        {domain.status === 'missing' && (
          <button
            onClick={onNavigateToChat}
            className="text-primary hover:underline font-medium flex items-center gap-1"
          >
            Add <ArrowUpRight className="h-3 w-3" />
          </button>
        )}
      </div>
    </Card>
  )
}
